import { TextField, Stack, Card, CardContent, CardHeader, Avatar, CardActions, IconButton, Tooltip, InputAdornment, Box, Typography } from "@mui/material";
import { ByWifiKeyDto } from "../../dtos/ByWifiKeyDto";
import { formatDate, formatDateTime } from "../../utils";
import { useContext, useState } from "react";
import { useVillageWifiHttp } from "./hooks/useVillageWifiHttp";
import VspPortalContext from "../../store/VspPortalContext";
import { LoadingButton } from "@mui/lab";
import UpdateIcon from '@mui/icons-material/Update';
import { EnterAuditDialog } from "./dialogs/EnterAuditDialog";
import dayjs from "dayjs";
import EditIcon from '@mui/icons-material/Edit';
import { WifiKeyExpiryDialog } from "./dialogs/WifiKeyExpiryDialog";
import { VillageWifiUserType } from "../../enums/VillageWifiUserType";
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { DeviceSessionDto } from "./dtos/DeviceSessionDto";
import { LatestDeviceSessions } from "./LatestDeviceSessions";


type ByWifiKeyDetailsProps = {

    byWifiKeyDto : ByWifiKeyDto;
    readOnly? : boolean;
    wifiKey : string;

};


const formControlSx = {

    label: {
        color: 'black !important',
        '.MuiTypography-root': {
            color: 'black !important'
        }
    }

};


const textFieldSx = {

    marginTop: '10px',
    width: '430px',
    label: {
        WebkitTextFillColor: 'black !important'
    },
    div: {
        input: {
            WebkitTextFillColor: 'black !important'
        }
    }
    
};


const getWifiUserType = ( byWifiKeyDto : ByWifiKeyDto ) : VillageWifiUserType => {

    if( byWifiKeyDto.employeeNumber ) {
        
        return VillageWifiUserType.Resident;

    }

    if( byWifiKeyDto.firstName && byWifiKeyDto.lastName ) {

        return VillageWifiUserType.Visitor;

    }

    return VillageWifiUserType.Device;

};


export const ByWifiKeyDetails: React.FC< ByWifiKeyDetailsProps > = ( props: ByWifiKeyDetailsProps ) => {

    const { byWifiKeyDto : initialByWifiKeyDto, readOnly, wifiKey } = props;

    const wifiUserType : VillageWifiUserType = getWifiUserType( initialByWifiKeyDto );

    const [ byWifiKeyDto, setByWifiKeyDto ] = useState< ByWifiKeyDto >( initialByWifiKeyDto ); 
    const [ expiryDialogOpen, setExpiryDialogOpen ] = useState< boolean >( false );
    const [ enterAuditTextDialogOpen, setEnterAuditTextDialogOpen ] = useState< boolean >( false );
    const [ isLoadingSessions, setIsLoadingSessions ]  = useState< boolean >( false );
    const [ latestDeviceSessions, setLatestDeviceSessions ] = useState< DeviceSessionDto[ ] >( [ ] );
    const [ isUpdating, setIsUpdating ] = useState< boolean >( false );


    const { getLatestDeviceSessions, updateByWifiKey } = useVillageWifiHttp( );
    const { state } = useContext( VspPortalContext );


    const handleDeviceSessionsClick = async( ) => {

        let residentId = initialByWifiKeyDto.id;

        // residentId = 547;

        setLatestDeviceSessions( [ ] );

        setIsLoadingSessions( true );
        const deviceSessionDtos = await getLatestDeviceSessions( residentId );
        setIsLoadingSessions( false );

        if(deviceSessionDtos ){

            setLatestDeviceSessions( deviceSessionDtos );

        }

    }


    const handleEnterAuditTextDialogCancel = ( ) => setEnterAuditTextDialogOpen( false );


    const handleEnterAuditTextDialogOk = async ( auditText : string ) => {

        setEnterAuditTextDialogOpen( false );

        setIsUpdating( true );

        const isSuccess = await updateByWifiKey({

            active : !byWifiKeyDto.isKeyActive
            , psk : wifiKey
            , pskAudit : auditText
            , updatedBy : state.username

        });

        setIsUpdating( false );

        if( isSuccess ){

            setByWifiKeyDto({ ...byWifiKeyDto
                , pskStatus: byWifiKeyDto.isKeyActive ? 'Suspended' : 'Active'
                , isKeyActive: !byWifiKeyDto.isKeyActive
                , pskAudit: auditText 
                , updatedBy : state.username
            });

        }

    };


    const handleEditExpiryClick = ( ) => setExpiryDialogOpen( true );


    const handleExpiryDialogCancel = ( ) => setExpiryDialogOpen( false );


    const handleExpiryDialogOk = async( expiry : string | undefined ) => {

        setExpiryDialogOpen( false );
        
        setIsUpdating( true );
        const isSuccess = await updateByWifiKey({

            active: byWifiKeyDto.isKeyActive
            , pskAudit: ''
            , keyExpiryDate : expiry
            , psk : wifiKey
            , updatedBy : state.username

        });
        setIsUpdating( false );

        if( isSuccess ){

            setByWifiKeyDto({ 
                ...byWifiKeyDto
                , keyExpiryDate: expiry ? new Date( expiry ) : undefined
            });

        }

    };

    
    const handleUpdateClick = ( ) => setEnterAuditTextDialogOpen( true );


    return(

        <>

            <Card sx={{ boxShadow: 5, marginTop: '15px !important', width: 'fit-content' }}>

                <CardHeader 
                    sx={{ paddingBottom: '0', paddingTop: '15px' }}
                    title={ `${byWifiKeyDto.asset} ${wifiUserType} Wi-Fi Key` }
                    titleTypographyProps={{ color:'dimgray', fontSize: '24px' }}
                    avatar={
                        <Avatar />
                    }
                />

                <CardContent>

                    <Stack>
                    
                        {
                            byWifiKeyDto.firstName
                                &&
                            <TextField 
                                sx={{ ...textFieldSx, ...formControlSx }}
                                label="First Name" 
                                variant="outlined"
                                value={ byWifiKeyDto.firstName }
                                disabled
                            />
                        }
                        
                        {

                            byWifiKeyDto.lastName
                                &&
                            <TextField
                                sx={ textFieldSx }
                                label="Last Name" 
                                variant="outlined"
                                value={ byWifiKeyDto.lastName }
                                disabled
                            />
                        }

                        {
                            byWifiKeyDto.employeeNumber
                                &&
                            <TextField 
                                sx={ textFieldSx }
                                label="Employee ID" 
                                variant="outlined"
                                value={ byWifiKeyDto.employeeNumber }
                                disabled
                            />
                        }

                        <TextField 
                            sx={ textFieldSx }
                            label="Status" 
                            variant="outlined"
                            value={ byWifiKeyDto.pskStatus }
                            disabled
                        />

                        <TextField 
                            sx={ textFieldSx }
                            label="Created By" 
                            variant="outlined"
                            value={ `${ byWifiKeyDto.createdBy } (${ formatDate( byWifiKeyDto.createdOn ) })` }
                            disabled
                        />

                        {
                        
                            byWifiKeyDto.comments
                                &&
                            <TextField 
                                sx={ textFieldSx }
                                label="First Comment" 
                                variant="outlined"
                                value={ byWifiKeyDto.comments }
                                disabled
                            />
                        }

                        { 
                            byWifiKeyDto.updatedBy
                                &&
                            <TextField 
                                sx={ textFieldSx }
                                label="Updated By" 
                                variant="outlined"
                                value={ `${ byWifiKeyDto.updatedBy } (${ formatDate( byWifiKeyDto.updatedOn ) })` }
                                disabled
                            />
                        }

                        {

                            byWifiKeyDto.pskAudit
                                &&
                            <TextField 
                                sx={ textFieldSx }
                                label="Last comment" 
                                variant="outlined"
                                value={ byWifiKeyDto.pskAudit }
                                disabled
                            />
                        }

                        {
                            wifiUserType !== VillageWifiUserType.Resident
                                &&
                            (
                                readOnly
                                    ?
                                <TextField 
                                    sx={ textFieldSx }
                                    label="Expiry" 
                                    variant="outlined"
                                    value={ byWifiKeyDto.keyExpiryDate ? formatDateTime( byWifiKeyDto.keyExpiryDate ) : 'Never' }
                                    disabled
                                />
                                    :
                                <TextField 
                                    sx={ textFieldSx }
                                    label="Expiry" 
                                    variant="outlined"
                                    value={ byWifiKeyDto.keyExpiryDate ? formatDateTime( byWifiKeyDto.keyExpiryDate ) : 'Never' }
                                    disabled
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton edge="end" onClick={ handleEditExpiryClick }>

                                                    <Tooltip
                                                        arrow
                                                        title={ <div style={{ whiteSpace: 'pre-line' }}> Edit Wi-Fi Key Expiry </div> }
                                                    >
                                                        <EditIcon sx={{ cursor: 'pointer'}} />
                                                    </Tooltip>
                                                
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            )
                        }

                    </Stack>

                </CardContent>
                
                {
                    !readOnly
                        &&
                    <CardActions
                        sx={{ display: "flex" }}
                    >

                        <LoadingButton
                            disabled={ isLoadingSessions || isUpdating }
                            loading={ isLoadingSessions }
                            loadingPosition="start"
                            onClick={ handleDeviceSessionsClick }
                            startIcon={ <SmartphoneIcon /> }
                            sx={{ fontWeight: 'bold', height: '47px', marginLeft: '10px', textTransform: 'none', width: '200px' }}
                            variant="contained"
                        >
                            Device
                        </LoadingButton>
                        
                        <Box sx={{ flexGrow: 1 }} />
            
                        <LoadingButton
                            disabled={ isLoadingSessions || isUpdating }
                            loading={ isUpdating }
                            loadingPosition="start"
                            onClick={ handleUpdateClick }
                            startIcon={ <UpdateIcon /> }
                            sx={{ fontWeight: 'bold', marginRight: '10px', textTransform: 'none', height: '47px', width: '200px' }}
                            variant="contained"
                        >
                            { byWifiKeyDto.isKeyActive ? 'Suspend' : 'Reactivate' }
                        </LoadingButton>

                    </CardActions>
                }
            </Card>

            {
                ( latestDeviceSessions.length > 0 )
                    &&
                <Stack sx={{ margin: '40px !important' }}>
                    <Typography align="center" sx={{ fontWeight: 'bold' }}>
                        Device List
                    </Typography>

                    <LatestDeviceSessions latestDeviceSessions={ latestDeviceSessions } />
                </Stack>
            }

            <WifiKeyExpiryDialog 
                dialogOpen={ expiryDialogOpen }
                handleCancel={ handleExpiryDialogCancel }
                handleOk={ handleExpiryDialogOk }
                initialExpiry={ dayjs( byWifiKeyDto.keyExpiryDate ) }
                wifiUserType={ wifiUserType }
            />

            <EnterAuditDialog 
                dialogOpen={ enterAuditTextDialogOpen }
                handleCancel={ handleEnterAuditTextDialogCancel }
                handleOk={ handleEnterAuditTextDialogOk }
            />

        </>

    );

};